import { ChangeDetectorRef, Directive, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FieldType } from '@ngx-formly/core';
import { SetValuesParams } from 'app/services/api5-service/api5.service';
import { equals } from 'ramda';
import { Observable } from 'rxjs';
import { distinctUntilChanged, skip, startWith, tap } from 'rxjs/operators';

import { DocHelper } from 'utils/dochelper';

import { IFormModel, ISetValueChanges } from '../dynamic-form.interface';
import { DynamicFormService } from '../dynamic-form.service';
import { DYNAMIC_FORM_DOC } from '../dynamic-form.utils';
import {
  ISPFieldType,
  ISPFieldConfig,
  ISPFormOptions,
  ISPFormState,
} from '../model';
import { SetValueEndEvent } from '../services/set-values.service';
import { isConfigWithControl } from '../utils/is-config-with-control';

/**
 * Base Formly field type class for Dragon theme
 */
@UntilDestroy()
@Directive()
export abstract class ISPFieldTypeBase<T extends ISPFieldType = ISPFieldType>
  extends FieldType<ISPFieldConfig<T>>
  implements OnInit
{
  get key(): string {
    return super.key as string;
  }

  get options(): ISPFormOptions {
    return super.options as ISPFormOptions;
  }

  get formState(): ISPFormState {
    return super.formState as ISPFormState;
  }

  get form(): FormGroup {
    return super.form as FormGroup;
  }

  protected readonly dynamicFormService: DynamicFormService = inject(
    DYNAMIC_FORM_DOC,
  ) as DynamicFormService;

  protected readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  /**
   * Subscribe to trigger event system and handle it
   */
  private subscribeToTriggerSystem(): void {
    const config = this.field;
    if (!isConfigWithControl(config)) {
      return;
    }

    this.formState.triggerService
      .getTriggerSetEvent$(config.props.originalControl.$name)
      .pipe(
        tap((value: IFormModel[string]) => {
          this.handleTriggerSetEvent(value);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  /**
   * Subscribe to set values requests response
   */
  private subscribeToSetValuesResponse(): void {
    this.formState.setValuesService.setValuesRequestEvent$
      .pipe(
        tap((event: SetValueEndEvent) => {
          if ('result' in event) {
            this.applySetValues(event.result, event.params);
          }
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  /**
   * Handle BE metadata, check for setvalues flag and subscribe to set values triggering if needed
   */
  private subscribeToSetValuesTriggering(): void {
    const config = this.field;
    if (!isConfigWithControl(config)) {
      return;
    }

    const setValuesType = config.props.originalControl.$setvalues;
    if (!setValuesType) {
      return;
    }

    const intervalSetValues =
      this.formState.setValuesService.getIntervalSetValues$(
        config.props.originalControl,
      );

    if (intervalSetValues) {
      intervalSetValues.pipe(untilDestroyed(this)).subscribe();
    } else {
      this.getSetValuesTriggerStream()
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.triggerSetValues();
        });
    }
  }

  /**
   * Get stream of control values, that should be sended to BE as setvalues. Can be overrided
   */
  protected getSetValuesTriggerStream(): Observable<IFormModel[string]> {
    return this.formControl.valueChanges.pipe(
      // @WARN it's important to emit and skip first value in order to distinctUntilChanged work on any change
      // because valueChanges do not emit first value, so distinctUntilChange have nothing to compare with
      startWith(this.formControl.value),
      distinctUntilChanged(),
      skip(1),
    );
  }

  /**
   * Trigger set values request. Can be overrided for additional setvalues params
   */
  protected triggerSetValues(): void {
    const config = this.field;
    if (!isConfigWithControl(config)) {
      return;
    }

    const setValuesType = config.props.originalControl.$setvalues;
    if (!setValuesType) {
      return;
    }

    if (config.props.originalControl.$svminlength) {
      const value = config.formControl?.value;
      const minLength = Number(config.props.originalControl.$svminlength);

      if (value.length < minLength) {
        return;
      }
    }

    const fieldName = config.props.originalControl.$name;
    this.formState.setValuesService
      .handleSetValues({
        setValuesType,
        fieldName,
        appendModel: true,
        showError: this.props.showSetValueError,
      })
      .subscribe();
  }

  /**
   * Apply set values response to field
   *
   * @param result - setvalues BE response
   * @param _params - setvalues BE params
   */
  protected applySetValues(
    result: ISetValueChanges,
    _params: SetValuesParams,
  ): void {
    const formControl = this.formControl;
    if (!formControl) {
      return;
    }

    const newValue = DocHelper.getValue(this.key, result.setValuesDoc);
    if (newValue === null) {
      return;
    }

    const originalValue = formControl.value;
    switch (true) {
      case typeof originalValue === 'string':
        formControl.setValue(newValue);
        break;
      case Array.isArray(originalValue): {
        const newValueArray = Array.isArray(newValue) ? newValue : [newValue];
        if (!equals([...originalValue].sort(), newValueArray.sort())) {
          formControl.setValue(newValueArray);
        }
        break;
      }
      case originalValue instanceof FileList:
        formControl.setValue(newValue);
    }
  }

  protected handleTriggerSetEvent(value: IFormModel[string]): void {
    const formControl = this.formControl;

    if (!formControl || !this.isValidTriggerValue(value)) {
      return;
    }

    formControl.setValue(value);
  }

  protected isValidTriggerValue(value: IFormModel[string]): boolean {
    return typeof value === 'string';
  }

  ngOnInit(): void {
    this.subscribeToSetValuesTriggering();
    this.subscribeToSetValuesResponse();
    this.subscribeToTriggerSystem();
  }
}
